import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { InsightItem } from "../components/InsightItem";
import { Space, Tag, Typography } from "antd";
import { ActivityItem } from "../components/ActivityItem";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface InsightData {
  type: string;
  description: string;
  evidence: string[];
  change?: string;
  theme?: string;
  contributors?: string[];
}

// Insights Block Recipe
const insightsRecipe = {
  id: "insights-block",
  name: "Insights Block",
  globalKey: [],
  prefix: `[
    {
      "type": "string — e.g. 'trend', 'anomaly', 'correlation'",
      "description": "string — detailed description of the insight",
      "evidence": ["string — supporting evidence or data points"],
      "change": "string — optional change indicator e.g. '+24%'",
      "theme": "string — optional theme of the insight",  
      "contributors": ["string"],
        }
    }
  ]`,
  format: {
    type: "json",
    fields: ["type", "description", "evidence"],
    constraints: [
      { field: "type", type: "string", required: true },
      { field: "description", type: "string", required: true },
      { field: "evidence", type: "array", required: true },
    ],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as InsightData[];
          return (
            Array.isArray(parsed) &&
            parsed.every(
              (item) =>
                item.type && item.description && Array.isArray(item.evidence)
            )
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// Insights Block Parser
function insightsParser(data: any) {
  try {
    return {
      data: data[insightsBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse insights data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// Insights Block Variants
const insightsVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard insights display with cards",
  },
  {
    id: "modern",
    name: "Modern",
    description: "Modern insights display with activity items",
  },
  {
    id: "list",
    name: "List",
    description: "Simple list display of insights",
  },
];

// Insights Block Renderer
function InsightsBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const insightsData = data[insightsBlock.id];

  if (!insightsData || insightsData.length === 0) return null;

  const renderDefaultCards = () => (
    <div className="responsive-card-grid">
      {insightsData.map((insight: InsightData, idx: number) => (
        <InsightItem
          key={`insight-${idx}`}
          type={insight.type || "trend"}
          description={insight.description}
          evidence={insight.evidence}
          change={insight.change ?? ""}
          variant="default"
          className="vzbl-panel-shadow vzbl-panel-overlay"
        />
      ))}
    </div>
  );

  const renderModernCards = () => (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {insightsData.map((insight: InsightData, idx: number) => (
        <ActivityItem
          key={`insight-${idx}`}
          description={insight.description}
          title={insight.theme}
          tags={insight.contributors}
          evidence={insight.evidence}
          cta={insight.change}
          className="vzbl-panel-overlay vzbl-panel-shadow"
        />
      ))}
    </Space>
  );

  const renderList = () => (
    <div
      className="vzbl-panel-overlay vzbl-panel-shadow"
      style={{ padding: "var(--padding-3)" }}
    >
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {insightsData.map((insight: InsightData, idx: number) => (
          <li
            key={`insight-${idx}`}
            style={{ marginBottom: "var(--padding-3)" }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              <Typography.Text strong className="vzbl-header-4">
                {insight.type}
              </Typography.Text>
              {insight.change && (
                <Tag className="vzbl-pill-tag">Change: {insight.change}</Tag>
              )}
            </div>
            <Markdown
              remarkPlugins={[remarkGfm]}
              skipHtml={true}
              className="vzbl-text-3"
            >
              {insight.description}
            </Markdown>
          </li>
        ))}
      </ul>
    </div>
  );

  const getRenderer = () => {
    switch (variant) {
      case "modern": {
        return renderModernCards;
      }
      case "list": {
        return renderList;
      }
      default: {
        return renderDefaultCards;
      }
    }
  };

  return (
    <div className="vzbl-panel-container">
      <Typography.Title level={4} className="vzbl-header-4">
        Insights
      </Typography.Title>
      {getRenderer()()}
    </div>
  );
}

// Create Insights Block
const insightsBlock = createBaseBlock({
  id: "insights",
  name: "Insights Block",
  description: "Displays insights and trends",
  recipe: insightsRecipe,
  variants: insightsVariants,
  parser: insightsParser,
  renderer: InsightsBlockRenderer,
});

// Export the block for direct use
export { insightsBlock };
